import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';

const log = createLogger('useReviewDataLogQuery');

export const QUERY_KEY = 'reviewDataLog';

export type DataLogEntry = {
  indication: string;
  context: string;
  timestamp: string;
  periodId: string;
};

export type ReviewDataLogResponse = {
  studentEvents: {
    [studentName: string]: DataLogEntry[];
  };
};

export function useReviewDataLog({ date, periodId }: { date: Date; periodId: string }) {
  const httpClient = useHttpClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status, refetch } = useQuery<null | ReviewDataLogResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching review data log', {
        day: params.day,
        periodId: params.periodId,
        timeZone: params.timeZone ?? 'unknown',
      });
      const { studentEvents } = await httpClient.get<ReviewDataLogResponse>(`/review/getDataLog?${paramsQueryStr}`);
      log.info('Fetched review data log successfully', {
        eventCount: Object.keys(studentEvents).length,
      });
      return { studentEvents };
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch review data log', failureCount, error, log }),
    enabled: false, // Don't fetch automatically, only when the user clicks the button
  });

  return { data, error, status, refetch };
}
